import React from 'react';
import { ResumeSearchResultsBody } from '@scale/shared-types';
import { Table, Typography, QueryBuilderType } from '@scale/scaleui';
import { ResumeHighlightedSection } from 'react-scale/components/GrowthCenter/ResumeSearch/ResumeHighlightedSection';
import { ViewResumeButton } from 'react-scale/components/GrowthCenter/ResumeSearch/ViewResumeButton';
import { ExportAllResultsButton } from 'react-scale/components/GrowthCenter/ResumeSearch/ExportAllResultsButton';
import { DownloadResumeButton } from 'react-scale/components/GrowthCenter/ResumeSearch/DownloadResumeButton';
import _ from 'lodash';

type ResumeSearchResultsTableProps = {
  data: ResumeSearchResultsBody[];
  count: number;
  queries: QueryBuilderType;
  isLoading: boolean;
  ids?: string[];
};

const ResumeSearchResultsTableColumns = [
  {
    header: 'Contributor',
    id: 'contributorId',
    enableSorting: false,
    cell: function contributorCell(data: any) {
      const { contributorId, email, firstName, lastName } = data.row.original;
      return (
        <div className="p-2">
          <Typography.Paragraph2 className="font-bold">
            {`${_.capitalize(firstName)} ${_.capitalize(lastName)}`}
          </Typography.Paragraph2>
          <Typography.Caption>Email: {email}</Typography.Caption>
          <Typography.Caption>ID: {contributorId} </Typography.Caption>
        </div>
      );
    },
  },
  {
    header: 'Worker Team',
    id: 'workerTeam',
    enableSorting: false,
    cell: function workerTeamCell(data: any) {
      return (
        <div className="flex flex-col whitespace-normal">
          <div className="break-words">{data.getValue()}</div>
        </div>
      );
    },
    size: 300,
    enableResizing: false,
  },
  {
    header: 'Search Result Preview',
    id: 'highlights',
    enableSorting: false,
    cell: function highlightsCell(data: any) {
      return <ResumeHighlightedSection highlights={data.getValue()} />;
    },
    size: 300,
    enableResizing: false,
  },
  {
    header: 'Last Active',
    id: 'lastActive',
    enableSorting: false,
    cell: function lastActiveCell(data: any) {
      const date = data.getValue()
        ? new Date(data.getValue()).toLocaleDateString('en-US', {
            timeZone: 'America/Los_Angeles',
          })
        : '';
      return <div>{date}</div>;
    },
  },
  {
    header: 'Resume',
    id: 'cdsLink',
    enableSorting: false,
    cell: function viewResumeCell(data: any) {
      const { firstName, lastName, contributorId } = data.row.original;
      return (
        <div className="w-full flex justify-center gap-2">
          <ViewResumeButton user={{ firstName, lastName, _id: contributorId }} />
          <DownloadResumeButton userId={contributorId} />
        </div>
      );
    },
  },
];

export const ResumeSearchResultsTable = ({
  data,
  count,
  queries,
  isLoading,
  ids,
}: ResumeSearchResultsTableProps) => {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-4 justify-between">
        <Typography.Paragraph1>Results ({count.toLocaleString()})</Typography.Paragraph1>
        <ExportAllResultsButton queries={queries} count={count} ids={ids} />
      </div>
      <div>Results are ranked based on relevance to query.</div>
      <Table
        columns={ResumeSearchResultsTableColumns}
        data={data}
        divideColumns
        divideRows
        stripedRows
        manualPagination
        isLoading={isLoading}
      />
    </div>
  );
};
